import React, { useState } from 'react';
import { Grid, Modal } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';

import CreateDatabaseModal from 'components/BiteDB/Modal/CreateDatabase';
import CreateProductDatabaseModal from 'components/BiteDB/Modal/CreateProductDatabase';
import HelpContentModal from 'components/BiteDB/Components/HelpContent';
import ToolBreadcrumb, {
  IToolBreadcrumbItem,
} from 'components/Common/ToolBreadcrumb';

interface IHeaderLayoutProps {
  children?: React.ReactNode;
  title: string;
  helpContent?: any;
  handleRefresh?: any;
  setTabState?: any;
  tabState?: string;
  setIsScroll?: any;
  onDatabaseCreated?: (databaseId: string) => void;
  onDatabaseUploadFailed?: (databaseId: string) => void;
  onOfferLinkEntities?: (databaseId: string) => void;
  databaseId?: string;
  htsCode?: string;
  // When set, renders a "🏠 hubLabel > ... > title" breadcrumb above the
  // page title for pages that drill into a specific record (e.g. a
  // database, supplier, or tariff), matching the Import Hub pattern.
  hubLabel?: string;
  hubTo?: string;
  // Optional dynamic middle crumb (e.g. the record's parent database, or
  // wherever the page was actually reached from) rendered between hubLabel
  // and title. Ignored when htsCode + databaseId are set, since that combo
  // already builds its own 3-level chain.
  middleCrumb?: {
    label: string;
    to?: string | { pathname: string; search?: string };
  };
  // Optional full breadcrumb chain for deeper record pages. When supplied it
  // takes precedence over the hub/middle/title shorthand above.
  breadcrumbItems?: IToolBreadcrumbItem[];
  // When true, skips the page's own <h1> title + help/create-database row
  // (breadcrumb still renders) — for pages that render an equivalent title
  // and help affordance themselves, merged into their own content.
  hideTitleRow?: boolean;
  // When true, hides the secondary "title + HTS code badge" row rendered
  // below the top ToolBreadcrumb — for pages where the breadcrumb already
  // shows that same title/HTS chain (e.g. Tariff, after it started passing
  // hubLabel/hubTo), so it isn't shown twice.
  hideSecondaryTitleRow?: boolean;
  // When true, hides the "? Help" button rendered alongside the secondary
  // title row, independent of hideSecondaryTitleRow.
  hideHelpButton?: boolean;
  // Optional style override for the top-level '.module-header' wrapper —
  // e.g. to match the ~32px header-to-breadcrumb spacing used by the
  // Analyzer detail pages' '.analyzers-dashboard-root' (Tariff is reached
  // from one of those run lists now, via hubLabel) instead of this layout's
  // default 6px margin-top, without changing that default for every other
  // DBModuleHeaderLayout caller.
  headerStyle?: React.CSSProperties;
}

const DBModuleHeaderLayout: React.FC<IHeaderLayoutProps> = (
  props: IHeaderLayoutProps,
) => {
  const {
    children,
    title,
    helpContent,
    handleRefresh,
    setTabState,
    tabState,
    setIsScroll,
    onDatabaseCreated,
    onDatabaseUploadFailed,
    onOfferLinkEntities,
    databaseId,
    htsCode,
    hubLabel,
    hubTo,
    middleCrumb,
    breadcrumbItems,
    hideTitleRow,
    hideSecondaryTitleRow,
    hideHelpButton,
    headerStyle,
  } = props;
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const [bulkProductUploadModalOpen, setBulkProductUploadModalOpen] =
    useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const location = useLocation<any>();
  const viewType =
    location.state?.objectData?.viewType || location.state?.viewType;
  const backLinkUrl =
    viewType === 'Analyzer'
      ? `/tariff-analyzers/${databaseId}`
      : `/database/${databaseId}`;

  // Helper for the Chevron separator
  const Chevron = () => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
    >
      <path
        d='M6 12L10 8L6 4'
        stroke='var(--bread-crumbs)'
        strokeWidth='1.33333'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );

  const digitsOnly = (s = '') => String(s).replace(/\D/g, '');

  const formatHtsPretty = (raw = '') => {
    const d = digitsOnly(String(raw));
    const parts = [d.slice(0, 4)];
    if (d.length > 4) {
      parts.push(d.slice(4, 6));
    }
    if (d.length > 6) {
      parts.push(d.slice(6, 10));
    }
    return parts.filter(Boolean).join('.');
  };

  return (
    <section
      className='home-content-body Home-HeaderDown'
      style={{ minHeight: 0 }}
    >
      <Grid container>
        <Grid item xs={12}>
          <div className='module-header' style={headerStyle}>
            {(breadcrumbItems || hubLabel) && (
              <ToolBreadcrumb
                items={
                  breadcrumbItems ||
                  (htsCode && databaseId
                    ? [
                        { label: hubLabel, to: hubTo },
                        { label: title, to: backLinkUrl },
                        { label: formatHtsPretty(htsCode) },
                      ]
                    : middleCrumb
                    ? [
                        { label: hubLabel, to: hubTo },
                        middleCrumb,
                        { label: title },
                      ]
                    : [{ label: hubLabel, to: hubTo }, { label: title }])
                }
              />
            )}
            {!hideTitleRow && (
              <div className='module-header-breadcrumbs'>
                {/*
                  This secondary title + HTS badge row duplicates the top
                  ToolBreadcrumb above once a page passes hubLabel/hubTo
                  (e.g. Tariff: "Tariff Analyzer > Tariff > 2829.90.6100"
                  shows there already). Tariff.tsx now passes
                  hideSecondaryTitleRow to suppress it below. Not removed
                  outright — every other DBModuleHeaderLayout caller
                  (Product, ReviewQ, Database, Screening, StatsRoll,
                  Supplier, BiteDB, EntityDB) still renders this normally.
                */}
                {!hideSecondaryTitleRow && (
                  <>
                    {title !== 'BITE DB' && htsCode && databaseId ? (
                      <Link to={backLinkUrl}>
                        <h1
                          className='analyzers-page-title'
                          style={{ cursor: 'pointer' }}
                        >
                          {title}
                        </h1>
                      </Link>
                    ) : (
                      title !== 'BITE DB' && (
                        <h1 className='analyzers-page-title'>{title}</h1>
                      )
                    )}
                    {htsCode && databaseId && <Chevron />}
                    {htsCode && databaseId && (
                      <>
                        <div className='bc-title' style={{ cursor: 'pointer' }}>
                          {formatHtsPretty(htsCode)}
                        </div>
                      </>
                    )}
                  </>
                )}

                <div className='module-header-title-conts'>
                  {/*
                    Help button — suppressed via hideHelpButton (currently
                    only passed by Tariff.tsx, for the same reason as the
                    secondary title row above). Left intact for every other
                    caller that still relies on it.
                  */}
                  {!hideHelpButton && helpContent && (
                    <div className='module-header-btns'>
                      <div
                        className='module-header-help'
                        onClick={() => setHelpModalOpen(true)}
                        role='button'
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            setHelpModalOpen(true);
                          }
                        }}
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='20'
                          height='20'
                          viewBox='0 0 20 20'
                          fill='none'
                        >
                          <g clipPath='url(#clip0_16034_866)'>
                            <path
                              d='M7.57484 7.50001C7.77076 6.94306 8.15746 6.47343 8.66647 6.17428C9.17547 5.87514 9.77392 5.76579 10.3558 5.8656C10.9377 5.96541 11.4655 6.26794 11.8457 6.71961C12.226 7.17128 12.4341 7.74294 12.4332 8.33334C12.4332 10 9.93317 10.8333 9.93317 10.8333M9.99984 14.1667H10.0082M18.3332 10C18.3332 14.6024 14.6022 18.3333 9.99984 18.3333C5.39746 18.3333 1.6665 14.6024 1.6665 10C1.6665 5.39763 5.39746 1.66667 9.99984 1.66667C14.6022 1.66667 18.3332 5.39763 18.3332 10Z'
                              stroke='#94969C'
                              strokeWidth='1.66667'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </g>
                          <defs>
                            <clipPath id='clip0_16034_866'>
                              <rect width='20' height='20' fill='white' />
                            </clipPath>
                          </defs>
                        </svg>
                        <p>Help</p>
                      </div>
                    </div>
                  )}
                  {
                    // Only show "Create Database" on the main BITE DB page
                    title.includes('BITE DB') && !databaseId && (
                      <>
                        <button
                          className='btn-new-automate'
                          onClick={() => {
                            if (tabState !== 'Products') {
                              setBulkUploadModalOpen(true);
                            } else {
                              setBulkProductUploadModalOpen(true);
                            }
                          }}
                        >
                          <div>Create Database</div>
                        </button>
                      </>
                    )
                  }
                </div>
              </div>
            )}
          </div>
        </Grid>
        <div className='HomeDashboard-container'>{children}</div>
      </Grid>
      <Modal
        open={bulkUploadModalOpen}
        onClose={() => setBulkUploadModalOpen(false)}
      >
        <CreateDatabaseModal
          open={bulkUploadModalOpen}
          handleClose={() => setBulkUploadModalOpen(false)}
          handleRefresh={handleRefresh}
          setTabState={setTabState}
          setIsScroll={setIsScroll}
          onDatabaseCreated={onDatabaseCreated}
          onDatabaseUploadFailed={onDatabaseUploadFailed}
          onOfferLinkEntities={onOfferLinkEntities}
        />
      </Modal>
      <Modal
        open={bulkProductUploadModalOpen}
        onClose={() => setBulkProductUploadModalOpen(false)}
      >
        <CreateProductDatabaseModal
          open={bulkProductUploadModalOpen}
          handleClose={() => setBulkProductUploadModalOpen(false)}
          handleRefresh={handleRefresh}
          setTabState={setTabState}
          onDatabaseCreated={onDatabaseCreated}
          onDatabaseUploadFailed={onDatabaseUploadFailed}
        />
      </Modal>
      <HelpContentModal
        isOpen={helpModalOpen}
        setIsOpen={setHelpModalOpen}
        title={helpContent?.title || 'Help'}
        description={helpContent?.description}
        helpItems={helpContent?.helpItems || []}
      />
    </section>
  );
};

export default DBModuleHeaderLayout;
